import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createUseStyles } from "react-jss";
import { useHistory } from "react-router-dom";
import MuiDataTable from "../../../components/Table/MuiDataTable/MuiDataTable";
import {
  Button,
  Caption,
  GridColumn,
  Grid,
  Spinner,
  useSnackbar,
} from "@livingdesign/react";
import {
  CS_SCOPES,
  CS_IS_ACTIVE_FILTER,
  CS_SCORE_SORT_OBJ,
  CS_DEFAULT_SORT_QUERY,
  EXPORT_MAX_LIMIT_CONF,
  INVOICE,
  RESUBMIT_ACTIONS,
  INVOICES_NEEDING_ACTION_FILTER
} from "../../../constants/constants";
import {
  ROWS_PER_PAGE_INVOICE,
  ROWS_PER_PAGE_OPTIONS_INVOICE,
  ENABLE_SERVER_SIDE_FEATURES_INVOICE,
} from "../../../constants/tableConfigs";
import { jsonToCsv, getExportedData, getErrorMessage, renderTernary, getColorVariantStatus } from "../../../helpers/utils";
import {
  getService,
  postService,
} from "../../../axios/axios";
import { Operations } from "../../../axios/operations";
import { setInvoiceData, resetNonAdjustedPaginationForLineItems, resetAdjustedPaginationForLineItems } from "../../../store/actions/invoiceActions";
import { COMPOSITE_SEARCH_SERVICE, INVOICE_SERVICE } from "../../../constants/baseURLs";
import { formSearchQuery, getAllowedActionsList, getInvoiceStatus, getPreValidationInvoiceStatus } from "../../../helpers/tableUtils";
import { displayLocalDate } from "../../../helpers/utcUtils";
import { formCompositeSearchQuery, formCsFilterObject, formCsPaginationQuery, formCsSortQuery, formPeopleCsFilterQuery, formRawSearchQuery, getUpdatedFilterQuery } from "../../../helpers/csQueryUtils";
import { FEATURES } from "../../../constants/features";
import { TableCellDisplay, amountFormat } from "../../../helpers/commonUtils";
import ExportModal from "../../../components/Modal/ExportModal/ExportModal";
import { getCurrentDate, getCurrentDateMMDDYY } from "../../../helpers/dateTimeUtils";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";

/**
 * styles used in the component.
 */
const useStyles = createUseStyles({
  dataTableWrap: {
    margin: "0",
    '& table > thead > tr > th.MuiTableCell-head': {
      color: '#000000',
      fontWeight: 'bold'
    },
    '& table > thead > tr > th.MuiTableCell-head button': {
      color: '#000000',
      fontWeight: 'bold'
    }
  },
  tabContainer: {
    "& nav > ul > li > a": {
      background: "none",
    }
  },
  starColumnHeader: {
    padding: "6px",
  },
  star: {
    padding: "6px",
  },
  handCursor: {
    cursor: "pointer",
  },
  percentChip: {
    width: '60px',
    height: '20px',
    display: 'block',
    margin: '0 auto',
    textAlign: 'center',
    borderRadius: '10px',
    background: '#E3E4E5'
  },
  exportName: {
    marginBottom: 20
  },
  exportNameAs: {
    paddingTop: 20,
    marginBottom: 20
  },
  modalSubText: {
    fontSize: 14,
    fontWeight: 'normal'
  },
  wd80: {
    width: '80px',
    display: "block",
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap'
  },
  wd100: {
    width: '100px',
    display: "block",
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap'
  },
  wd120: {
    width: '120px',
    display: "block",
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap'
  },
  wd60: {
    width: '60px',
    display: "block",
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap'
  },
  footer: {
    position: "fixed",
    bottom: 0,
    paddingBottom: 10,
    color: "#515357",
    fontSize: "14px",
    marginTop: "20px"
  },
  spark: {
    background: '#ffc22057',
    color: '#815e00',
    border: '1.5px solid #ffc220',
    padding: '4px 10px',
    borderRadius: '4px',
    display: "inline-block",
    margin: '3px 0'
  },
  hyperLink: {
    color: "#0071dc",
    cursor: "pointer"
  },
});

/**
 * Gets called when user download the export file
 * client side
 * @public
 */
export const exportClientData = (invoices, exportSelection, rowsData) => {
  let csvData = jsonToCsv(invoices);
  if (exportSelection === 'currentSelection') {
    csvData = jsonToCsv(rowsData);
  } else if (exportSelection === 'currentPage') {
    csvData = jsonToCsv(invoices);
  }
  //Download the file as CSV
  const url = window.URL.createObjectURL(
    new Blob([csvData]),
  );
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `invoice-data.csv`);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
}



/**
 * Invoice component to list the invoice under financial component
 */
const FinancialInvoiceList = (props) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { currentFinancialTab, fromUploadScreen, setIsCurrentTab } = props;
  const invoiceAccessibility = useSelector((state) => state?.accessibility?.applicationAccess?.financial?.invoice);
  const orgIdentifier = useSelector((state) => state?.organization?.organizationDetailValues?.identifier);
  const featureMap = useSelector((state) => state?.user?.featureMap);
  const isPreValidationOrg = useSelector((state) => state?.user?.prevalidationEnabled);

  const renderMatterName = (value) => {
    return <Caption as="p" UNSAFE_className={classes.wd120}>{value}</Caption>
  }

  const renderMatterNumber = (value) => {
    return <Caption as="p" UNSAFE_className={classes.wd120} id={`matter-number-${value}`}>{value}</Caption>
  }

  const renderCreatedReceievedDate = (value, tableMeta) => {
    return (
      tableMeta?.rowData[2] !== true ? (
        <Caption as="p">
          {displayLocalDate(value)}
        </Caption>
      ) : null
    )
  }

  const renderStatus = (value, tableMeta) => {
    const displayStatus = getPreValidationInvoiceStatus(value);
    const color = getColorVariantStatus(value)
    return <Caption as="p" id={`table-row-${value}-${tableMeta?.rowIndex}`}>{displayStatus ? <span className={classes?.[color]}>{displayStatus}</span> : displayStatus}</Caption>
  }

  // const renderInvoiceNumber = (value, tableMeta) => {

  // }

  const COLUMNS = [
    {
      name: "organizationInvoiceNumber",
      label: "Invoice Number",
      colKey: "organizationInvoiceNumber",
      options: {
        sort: true,
        display: true,
        setCellHeaderProps: () => ({ style: { minWidth: "102px" } }),
        setCellProps: () => ({ style: { minWidth: "102px" } }),
        customBodyRender: (value, tableMeta) => {
          const status = tableMeta?.rowData?.[4];
          const color = getColorVariantStatus(status);
          return TableCellDisplay(`table-row-${tableMeta?.rowIndex}`, color ? <span className={classes?.[color]}>{value}</span> : value);
        },
        customFilterListOptions: { render: (v) => `Invoice Number: ${v}` },
      },
      operation: Operations.LIKEIC,
      type: "tag"
    },
    {
      name: "invoiceDate",
      label: "Invoice Date",
      colKey: "invoiceDate",
      options: {
        sort: true,
        display: true,
        setCellProps: () => ({
          style: {
            whiteSpace: "nowrap"
          }
        }),
        setCellHeaderProps: () => ({
          style: {
            whiteSpace: "nowrap"
          }
        }),
        customBodyRender: (value, tableMeta) => {
          return (
            tableMeta?.rowData[2] !== true ? (
              renderTernary(value, displayLocalDate(value), '-')
            ) : null
          )
        },
      },
    },
    {
      name: "createdTs",
      label: "Created Date",
      colKey: "createdTs",
      options: {
        sort: true,
        display: true,
        setCellProps: () => ({
          style: {
            whiteSpace: "nowrap"
          }
        }),
        setCellHeaderProps: () => ({
          style: {
            whiteSpace: "nowrap"
          }
        }),
        customBodyRender: renderCreatedReceievedDate,
        customFilterListOptions: {
          render: (v) => {
            return `Invoice Date Min-Max Date: ${v[0]} - ${v[1]}`;
          },
          update: (filterList, filterPos, index) => {
            if (filterPos === -1) {
              filterList = [];
            }
            return filterList;
          },
        },
        filter: true,
        filterType: 'custom',
        filterList: [],
        filterOptions: {
          names: [],
          logic(date, filters) {
            if (filters[0] && filters[1]) {
              return date < filters[0] || date > filters[1];
            } else if (filters[0]) {
              return date < filters[0];
            } else if (filters[1]) {
              return date > filters[1];
            }
            return false;
          }
        }
      },
      operation: Operations.GTE,
      type: "date"
    },
    {
      name: "receivedDate",
      label: "Received Date",
      colKey: "receivedDate",
      options: {
        sort: true,
        display: false,
        setCellProps: () => ({
          style: {
            whiteSpace: "nowrap"
          }
        }),
        setCellHeaderProps: () => ({
          style: {
            whiteSpace: "nowrap"
          }
        }),
        customBodyRender: renderCreatedReceievedDate,
      },
      operation: Operations.GTE,
      type: "date"
    },
    {
      name: "status",
      label: "Invoice Status",
      colKey: "status",
      options: {
        display: true,
        customBodyRender: renderStatus,
        customFilterListOptions: { render: (v) => `Status: ${v}` },
      },
      operation: Operations.EQ,
      type: "id"
    },
    {
      name: "invoiceType",
      label: "Invoice Type",
      colKey: "invoiceType",
      options: {
        display: false,
      },
    },
    {
      name: "totalNetAmount",
      label: "$ Current Total",
      colKey: "totalNetAmount",
      options: {
        display: true,
        setCellHeaderProps: () => ({ style: { minWidth: "80px" } }),
        setCellProps: () => ({ style: { minWidth: "80px" } }),
        customBodyRender: (value, tableMeta) => TableCellDisplay(`table-row-totalNetAmount-${tableMeta.rowIndex}`, `$${amountFormat(value)}`),
        customFilterListOptions: { render: (v) => `Total: ${v}` },
      },
      operation: Operations.EQ,
      type: "number"
    },
    {
      name: "assignedTo",
      label: "Assigned To",
      colKey: "assignedTo",
      options: {
        display: false,
        customBodyRender: (value, tableMeta) => TableCellDisplay(`table-row-assignedTo-${tableMeta.rowIndex}`, value),
      },
      operation: Operations.LIKE,
      type: "tag"
    },
    {
      name: "matterName",
      label: "Matter",
      colKey: "matterName",
      options: {
        sort: true,
        display: true,
        setCellHeaderProps: () => ({ style: { minWidth: "80px" } }),
        setCellProps: () => ({ style: { minWidth: "80px" } }),
        customBodyRender: renderMatterName,
        customFilterListOptions: { render: (v) => `Matter: ${v}` },
      },
      operation: Operations.LIKEIC,
      type: "tag"
    },
    {
      name: "matterNumber",
      label: "Matter Number",
      colKey: "matterNumber",
      options: {
        sort: true,
        display: true,
        setCellHeaderProps: () => ({ style: { minWidth: "100px" } }),
        setCellProps: () => ({ style: { minWidth: "100px" } }),
        customBodyRender: renderMatterNumber,
        customFilterListOptions: { render: (v) => `Matter Number: ${v}` },
      },
      operation: Operations.LIKEIC,
      type: "tag"
    },
  ];

  const dispatch = useDispatch();
  const { invoiceData } = useSelector((state) => state?.invoice);
  const [invoiceColumns, setInvoiceColumns] = useState(COLUMNS);
  const [loading, setLoading] = useState(false);
  const [rowsPerPageInvoice, setRowsPerPageInvoice] = useState(ROWS_PER_PAGE_INVOICE);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterQuery, setFilterQuery] = useState("");
  const [sortQuery, setSortQuery] = useState(CS_DEFAULT_SORT_QUERY);
  const [exportLoading, setExportLoading] = useState(false);
  const [createExportLoading, setCreateExportLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectRowsData, setSelectRowsData] = useState([]);
  const [rowsData, setRowsData] = useState([]);
  const showExportAllOption = invoiceData?.count < EXPORT_MAX_LIMIT_CONF.INVOICE;
  const defaultExportOption = showExportAllOption ? 'allInvoice' : 'currentPage';
  const [exportSelection, setExportSelection] = useState(defaultExportOption);
  const [currentPageExport, setCurrentPageExport] = useState(false);
  const [needAction, setNeedAction] = useState(false);
  const [needActionCount, setNeedActionCount] = useState(0);
  const [needActionLoading, setNeedActionLoading] = useState(false);
  const [page, setPage] = useState(0)
  const [searchText, setSearchText] = useState("")

  const defaultQuery = {
    "filters": [
      CS_IS_ACTIVE_FILTER,
      formCsFilterObject("organizationId", "match", orgIdentifier),
      formCsFilterObject("invoiceType", "in", ["Invoice", "Refund", "Recovery"])
    ],
    "operation": "AND",
    "properties": null
  }

  const { addSnack } = useSnackbar();

  const FILTER_CONFIGS = [
    {
      colKey: "organizationInvoiceNumber",
      type: "textField",
      defaultOpen: true,
      displayLabel: true,
      placeholder: 'Search an invoice number',
      accordian: 'Invoice Number',
    },
    {
      colKey: "status",
      type: "checkbox",
      label: "Status",
      options: isPreValidationOrg ? [
        "Appeal Pending",
        "Created",
        "Not Submitted",
        "Paid",
        "Processing Payment",
        "Rejected",
        "Void",
        "Workflow"
      ] :
        [
          "Appeal Pending",
          "Paid",
          "Processing Payment",
          "Rejected",
          "Void",
          "Workflow"
        ],
      defaultOpen: true,
      displayLabel: false,
      accordian: 'Status'
    },
    {
      colKey: "totalNetAmount",
      type: "textField",
      defaultOpen: true,
      accordian: 'Current Total $',
      placeholder: 'Search an amount',
      isPositiveDecimalNumber: true
    },
    {
      colKey: "createdTs",
      type: "rangePicker",
      label: "Start Date",
      defaultOpen: true,
      section: "right",
      displayLabel: true,
      accordian: 'Created Date Range'
    },
    {
      colKey: "matterNumber",
      type: "matterDropdownAutocomplete",
      label: "Matter Number",
      defaultOpen: true,
      displayLabel: false,
      accordian: 'Matter Number',
      id: "matterNumber",
      isRestricted: false
    },
    {
      colKey: "matterName",
      type: "matterDropdownAutocomplete",
      label: "Matter Name",
      defaultOpen: true,
      displayLabel: false,
      accordian: 'Matter Name',
      id: "matterName",
      isRestricted: false
    }
  ];


  /**
   * Function to call Invoice Search Service
   * @param {string} query
   */
  const getinvoiceData = (paginationQuery, preAppliedFilter) => {
    setLoading(true);
    const sortQueryInvoice = { ...sortQuery };
    if (searchQuery !== null && searchQuery !== undefined && Object.keys(searchQuery).length !== 0 && JSON.stringify(sortQuery) === JSON.stringify(CS_DEFAULT_SORT_QUERY)) {
      sortQueryInvoice.sortBy = [CS_SCORE_SORT_OBJ, ...sortQuery?.sortBy];
    }
    const updatedfilterQuery = preAppliedFilter ? preAppliedFilter : getUpdatedFilterQuery({ ...filterQuery });
    let query = {};
    if (needAction) {
      const defaultNeedActionQuery = {
        "filters": [
          CS_IS_ACTIVE_FILTER,
          INVOICES_NEEDING_ACTION_FILTER,
          formCsFilterObject("organizationId", "match", orgIdentifier),
          formCsFilterObject("invoiceType", "in", ["Invoice", "Refund", "Recovery"])
        ],
        "operation": "AND",
        "properties": null
      }
      query = formCompositeSearchQuery([CS_SCOPES.INVOICE], updatedfilterQuery, searchQuery, sortQueryInvoice, paginationQuery, {}, defaultNeedActionQuery, {});
    }
    else {
      query = formCompositeSearchQuery([CS_SCOPES.INVOICE], updatedfilterQuery, searchQuery, sortQueryInvoice, paginationQuery, {}, defaultQuery, {});
    }
    let serviceEndPoint = `/composite-search/v1?returnCsv=false&organizationId=${orgIdentifier}`;
    // add flag to fetch matter id list for logged in user
    if (!invoiceAccessibility?.viewAll) {
      serviceEndPoint += '&fetchMatterIdListForUserId=true';
    }
    postService(
      COMPOSITE_SEARCH_SERVICE,
      serviceEndPoint,
      query
    )
      .then((response) => {
        dispatch(setInvoiceData({
          invoices: response?.data?.invoice?.data,
          count: response?.data?.invoice?.count
        }));
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        setNeedActionLoading(false)
        addSnack({
          message: getErrorMessage(error)
        });
      });
  };

  /**
   * Function to call Invoice Search Service to get need action count
   * @param {string} query
   */
  const getNeedActionCount = () => {
    setNeedActionLoading(true)
    let query = {};
    const defaultNeedActionQuery = {
      "filters": [
        CS_IS_ACTIVE_FILTER,
        INVOICES_NEEDING_ACTION_FILTER,
        formCsFilterObject("organizationId", "match", orgIdentifier),
        formCsFilterObject("invoiceType", "in", ["Invoice", "Refund", "Recovery"])
      ],
      "operation": "AND",
      "properties": null
    }
    query = formCompositeSearchQuery([CS_SCOPES.INVOICE], {}, {}, {}, {}, {}, defaultNeedActionQuery, {});
    let serviceEndPoint = `/composite-search/v1?returnCsv=false&organizationId=${orgIdentifier}`;
    // add flag to fetch matter id list for logged in user
    if (!invoiceAccessibility?.viewAll) {
      serviceEndPoint += '&fetchMatterIdListForUserId=true';
    }
    postService(
      COMPOSITE_SEARCH_SERVICE,
      serviceEndPoint,
      query
    )
      .then((response) => {
        setNeedActionLoading(false)
        setNeedActionCount(response?.data?.invoice?.count)
      })
      .catch((error) => {
        setNeedActionLoading(false)
      });
  };

  /**
   * Function to handle row click
   * @param {object} rowData: row data
   * @param {object} rowMeta: meta data of selected row
   */
  const handleRowClick = (rowData, rowMeta) => {
    const { id } = invoiceData?.invoices[rowMeta?.rowIndex];
    history.push(`/invoice/${id}`, { invoiceData: invoiceData?.invoices[rowMeta?.rowIndex], currentFinancialTab: currentFinancialTab })
  }

  /**
   * Function to validate search query and set query or show no results UI
   * @param {string} searchText
   */
  const handleServerSideSearch = (searchText) => {
    const searchQuery = formRawSearchQuery(searchText);
    setSearchText(searchText);
    setSearchQuery(searchQuery);
  };

  /**
   * Function to set filter query on filter action
   * @param {string} query
   */
  const handleServerSideFilter = (query) => {
    setFilterQuery(query);
  };

  /**
   * Function to call api on pagination action
   * @param {number} rowsPerPage
   * @param {number} page
   */
  const handleServerSidePagination = ({ rowsPerPage, page }) => {
    setRowsPerPageInvoice(rowsPerPage);
    setPage(page);
    const paginationQuery = formCsPaginationQuery(rowsPerPage, (rowsPerPage * page));
    getinvoiceData(paginationQuery);
  };

  const refreshInvoices = () => {
    const paginationQuery = formCsPaginationQuery(rowsPerPageInvoice, (rowsPerPageInvoice * page));
    getinvoiceData(paginationQuery);
    getNeedActionCount();
  }
  /**
   * Function to call api on sort action
   * @param {number} rowsPerPage
   * @param {number} page
   */
  const handleServerSideSort = ({ name, direction }) => {
    const sortQuery = formCsSortQuery(name, direction);
    setSortQuery(sortQuery);
  };

  const invoiceListTableCustomOptions = {
    downloadOptions: {
      filename: "invoice",
    },
    responsive: "standard",
    rowsPerPage: rowsPerPageInvoice,
    isInvoice: true,
    rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS_INVOICE,
    filterConfigs: FILTER_CONFIGS,
    serverSide: ENABLE_SERVER_SIDE_FEATURES_INVOICE,
    onServerSideSearch: handleServerSideSearch,
    onServerSideFilter: handleServerSideFilter,
    onServerSidePagination: handleServerSidePagination,
    onServerSideSort: handleServerSideSort,
    totalCount: invoiceData?.count,
    searchText: searchText
  };

  /**
   * Cancel Export Modal
   */
  const onCancelBtnClk = () => {
    setExportLoading(false)
    setModalOpen(false)
  }

  /**
  * Gets called when user click on export button
  *
  * @public
  */
  const handleExport = () => {
    setExportSelection(defaultExportOption)
    setModalOpen(true);
    setExportLoading(true);
    setCurrentPageExport(true);
  };

  const handleExportAdjustmentSummary = () => {
    setExportLoading(true);
    const query = encodeURIComponent(formSearchQuery("id", Operations.IN, rowsData?.map((item) => item?.id), 'array'));
    getService(
      INVOICE_SERVICE,
      `/search/invoices/adjustment-summary/v1?filter=${query}&csv=true`
    )
      .then((response) => {
        const data = response?.data;
        const contentType = response?.headers["content-type"];
        getExportedData(data, contentType, `WMMS_Adjustment summary_${getCurrentDateMMDDYY()}`);
        setExportLoading(false);
      })
      .catch((error) => {
        setExportLoading(false);
        setCreateExportLoading(false);
        addSnack({
          message: getErrorMessage(error)
        });
      })
  }

  /**
   * function will use when row is select/deselect
   */
  const handleRowSelectionChange = (currentSelect, allSelected, selectRowsData) => {
    const bData = invoiceData?.invoices;
    const result = allSelected?.map(item => {
      return bData && bData[item?.index]
    });
    setSelectRowsData(selectRowsData);
    setRowsData(result);
  }

  /**
   * After click on create button download Actions
   */
  const onClickCreate = () => {
    setCreateExportLoading(true);
    if (!ENABLE_SERVER_SIDE_FEATURES_INVOICE) {
      exportClientData(invoiceData?.invoices, exportSelection, rowsData);
      setModalOpen(false);
      setExportLoading(false);
      setCreateExportLoading(false);
    } else {
      let exportQuery = {};
      if (needAction) {
        const defaultNeedActionQuery = {
          "filters": [
            CS_IS_ACTIVE_FILTER,
            INVOICES_NEEDING_ACTION_FILTER,
            formCsFilterObject("organizationId", "match", orgIdentifier),
            formCsFilterObject("invoiceType", "in", ["Invoice", "Refund", "Recovery"])
          ],
          "operation": "AND",
          "properties": null
        }
        exportQuery = formCompositeSearchQuery([CS_SCOPES.INVOICE], filterQuery, searchQuery, sortQuery, {}, { getAll: true }, {}, defaultNeedActionQuery);
      } else {
        exportQuery = formCompositeSearchQuery([CS_SCOPES.INVOICE], filterQuery, searchQuery, sortQuery, {}, { getAll: true }, {}, defaultQuery);
      }
      if (exportSelection === 'currentSelection') {
        const result = rowsData?.map(a => a?.id);
        const selectionQuery =
        {
          "filters": [formCsFilterObject("id", "in", result)],
          "operation": "AND",
          "properties": null
        }
        exportQuery = formCompositeSearchQuery([CS_SCOPES.INVOICE], {}, {}, {}, {}, { getAll: true }, {}, defaultQuery, selectionQuery);
      } else if (exportSelection === 'currentPage') {
        const result = invoiceData?.invoices?.map(a => a?.id);
        const selectionQuery =
        {
          "filters": [formCsFilterObject("id", "in", result)],
          "operation": "AND",
          "properties": null
        }
        exportQuery = formCompositeSearchQuery([CS_SCOPES.INVOICE], {}, {}, {}, {}, { getAll: true }, {}, defaultQuery, selectionQuery);
      }
      let serviceEndPoint = `/composite-search/v1?returnCsv=true&organizationId=${orgIdentifier}`;
      // add flag to fetch matter id list for logged in user
      if (!invoiceAccessibility?.viewAll) {
        serviceEndPoint += '&fetchMatterIdListForUserId=true';
      }
      postService(
        COMPOSITE_SEARCH_SERVICE,
        serviceEndPoint,
        exportQuery
      )
        .then((response) => {
          const data = response?.data;
          const contentType = response?.headers["content-type"];
          getExportedData(data, contentType, 'invoice');
          setModalOpen(false);
          setExportLoading(false);
          setCreateExportLoading(false);
        })
        .catch((error) => {
          setModalOpen(false);
          setExportLoading(false);
          setCreateExportLoading(false);
          addSnack({
            message: getErrorMessage(error)
          });
        });
    }
  }

  /**
   * Add new action items
   */
  const addNewActionItems = [
    {
      "actionId": 0,
      "label": "Add New Invoice",
      "isAboveDivider": true,
      "showAction": true
    },
    {
      "actionId": 1,
      "label": "Upload Invoices",
      "isAboveDivider": true,
      "showAction": true
    }
  ]

  useEffect(() => {
    if (loading || fromUploadScreen) { return }
    const result = {};
    [...invoiceColumns].forEach(item => {
      result[item?.colKey] = item?.options?.filterList;
    })
    const filterQuery = formPeopleCsFilterQuery(result);
    setFilterQuery(filterQuery);
  }, [invoiceColumns])

  /**
   * To load initial data and handle search/filter/sort query change on Invoice datatable
   */
  useEffect(() => {
    if (loading || fromUploadScreen) { return }
    const paginationQuery = {
      "offset": 0,
      "limit": rowsPerPageInvoice,
    }
    getinvoiceData(paginationQuery);
  }, [searchQuery, filterQuery, sortQuery, needAction]);

  useEffect(() => {
    const paginationQuery = {
      "offset": 0,
      "limit": rowsPerPageInvoice,
    }
    if (fromUploadScreen) {
      // Apply pre-applied filters
      const updatedColumns = [...invoiceColumns];
      updatedColumns.forEach((item) => {
        if (item.colKey === "createdTs") {
          item.options.filterList = [getCurrentDate(), getCurrentDate()];
        }
        if (item.colKey === "status") {
          item.options.filterList = ["Not Submitted"];
        }
      });

      // Trigger API call with pre-applied filters
      const result = {};
      updatedColumns.forEach((item) => {
        result[item?.colKey] = item?.options?.filterList;
      });
      const preAppliedFilter = formPeopleCsFilterQuery(result);
      getinvoiceData(paginationQuery, preAppliedFilter);
      setIsCurrentTab(1, false)
      // Reset the fromUploadScreen flag
      history.replace({
        ...location,
        state: { ...location.state, fromUploadScreen: false },
      });
    }
  }, [fromUploadScreen]);

  /**
   * Component Unmount
   */
  useEffect(() => {
    getNeedActionCount()
    dispatch(resetNonAdjustedPaginationForLineItems())
    dispatch(resetAdjustedPaginationForLineItems())
  }, []);

  const resetSelectedRows = () => {
    setSelectRowsData([]);
    setRowsData([]);
  }

  const exportOptions = [
    {
      "actionId": 0,
      "label": "Invoices",
      "isAboveDivider": true,
      "disabled": false
    },
    {
      "actionId": 1,
      "label": "Adjustment Summary",
      "isAboveDivider": true,
      "disabled": rowsData.length === 0 || loading || rowsData?.some(row => row?.status !== "Draft" && row?.status !== "Appealed")
    }
  ]

  const handleMultiExport = (id, tableMeta) => {
    switch (id) {
      case 0:
        handleExport()
        break;
      case 1:
        handleExportAdjustmentSummary()
        break;
      default:
        handleExport()
        break;

    }
  }

  const clearPreAppliedFilters = () => {
    const cols = [...invoiceColumns];
    cols.forEach((item) => {
      item.options.filterList = [];
    });
    setSearchQuery({});
    setFilterQuery({});
    setSearchText("");
    setInvoiceColumns(cols);
  }

  const filterButton = () => {
    return (
      <Button
        size="small"
        variant="secondary"
        data-testid="needaction-button"
        id="needaction-button"
        onClick={() => { setNeedAction(!needAction); clearPreAppliedFilters() }}
        UNSAFE_style={
          needAction
            ? {
              margin: "0 0.8rem",
              background: 'red',
              color: "white",
              boxShadow: 'none',
            }
            : {
              margin: "0 0.8rem",
              color: "red",
              background: 'white',
              boxShadow: "inset 0 0 0 .0625rem red",
            }
        }
      >
        Need action {needActionLoading ? <Spinner size='small' color="red" /> : `(${needActionCount})`}
      </Button>
    );
  };

  const bulkUpdateOptions = {
    actionItems: RESUBMIT_ACTIONS,
    disableButton: rowsData.length === 0 || loading || rowsData?.some(row => row?.status !== "Draft" && row?.status !== "Appealed"),
    invoiceIds: rowsData,
    reloadPage: refreshInvoices
  }
  const bulkDeleteOptions = {
    disableButton: rowsData?.length === 0 || loading || rowsData?.some(row => row?.status !== "Draft"),
    invoiceIds: rowsData,
    reloadPage: refreshInvoices
  }

  return (
    <>
      <Grid hasGutter={true}>
        <GridColumn>
          <div className={classes.dataTableWrap}>
            <MuiDataTable
              data={invoiceData?.invoices}
              columns={invoiceColumns}
              setTableColumns={setInvoiceColumns}
              customOptions={invoiceListTableCustomOptions}
              selectableRowsType={"multiple"}
              onRowClick={handleRowClick}
              groupFilter={true}
              exportLoading={exportLoading}
              onExportClick={handleExport}
              selectedRows={selectRowsData}
              onRowSelection={handleRowSelectionChange}
              loading={loading}
              showRowsCounter={false}
              customFilterButton={isPreValidationOrg ? filterButton() : <></>}
              allowedActions={getAllowedActionsList(invoiceAccessibility)}
              resetSelectedRows={() => resetSelectedRows()}
              showCustomAddNewButton={featureMap?.[FEATURES.BULK_JOB] || false}
              hideAddNewButton={true}
              showBulkUpdateButton={isPreValidationOrg}
              bulkUpdateButtonOptions={bulkUpdateOptions}
              showBulkDeleteButton={featureMap?.[FEATURES.INVOICE_PRE_VALIDATION]}
              bulkDeleteButtonOptions={bulkDeleteOptions}
              exportOptions={exportOptions}
              handleMultiExport={handleMultiExport}
            />
          </div>
        </GridColumn>
        <GridColumn UNSAFE_className={classes.footer}>
          Can’t find an invoice that was uploaded? Please refer <a className={classes.hyperLink} data-testid="process-status-link" onClick={() => setIsCurrentTab(2)}>Process status</a> tab for updates on invoice creation uploaded via LEDES file(s).
        </GridColumn>
      </Grid>
      <ExportModal
        title={INVOICE}
        mode={modalOpen}
        onCancel={onCancelBtnClk}
        onCreate={onClickCreate}
        rowsData={rowsData}
        showExportAllOption={showExportAllOption}
        exportSelection={exportSelection}
        setExportSelection={setExportSelection}
        currentPageExport={currentPageExport}
        setCurrentPageExport={setCurrentPageExport}
        exportLoading={createExportLoading}
      />
    </>
  );

}

export default FinancialInvoiceList;
